// Transient confirmations, with an optional Undo.
//
// This exists so that reversible actions can just *happen*. A confirm dialog
// asks the user to be certain before anything is known; an Undo lets them act
// and then see the result, which is the only point at which they can actually
// judge it. Bulk approve is unusable under the first model and fine under the
// second.
//
// Undo here is a *compensating action*, never a deferred send: the mutation has
// already run and committed by the time the toast appears. An admin who approves
// and closes the tab gets an approval.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { AlertCircle, CheckCircle2, Info, X } from "lucide-react";

export type ToastTone = "default" | "success" | "error";

export type ToastAction = {
  label: string;
  onAction: () => void | Promise<void>;
};

export type ToastOptions = {
  tone?: ToastTone;
  /** Milliseconds before auto-dismiss. `0` keeps it until dismissed. */
  duration?: number;
  action?: ToastAction;
};

type Toast = ToastOptions & {
  id: string;
  message: ReactNode;
  tone: ToastTone;
  duration: number;
};

type ToastApi = {
  toast: (message: ReactNode, options?: ToastOptions) => string;
  dismiss: (id: string) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

const DEFAULT_DURATION = 6_000;
/** Long enough to notice, reach for, and click — an Undo that expires first is decoration. */
const ACTION_DURATION = 10_000;

/**
 * `useToast` outside the provider is a wiring mistake, not a user-facing one.
 * It logs rather than throws: a missing confirmation should never be the thing
 * that takes down a screen whose actual work already succeeded.
 */
export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  return (
    context ?? {
      toast: (message) => {
        console.warn("useToast called outside <ToastProvider>:", message);
        return "";
      },
      dismiss: () => {},
    }
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((message: ReactNode, options: ToastOptions = {}) => {
    const id = crypto.randomUUID();
    setToasts((current) => [
      ...current,
      {
        id,
        message,
        tone: options.tone ?? "default",
        duration:
          options.duration ??
          (options.action ? ACTION_DURATION : DEFAULT_DURATION),
        action: options.action,
      },
    ]);
    return id;
  }, []);

  const api = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

/**
 * Both live regions are mounted permanently and start empty.
 *
 * A live region that is inserted into the DOM *already containing* its text is
 * not announced by most screen readers — the region has to exist first for the
 * change to be a change. `aria-live` sits here and never on an individual toast,
 * or every message gets announced twice.
 */
function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}) {
  const polite = toasts.filter((t) => t.tone !== "error");
  const assertive = toasts.filter((t) => t.tone === "error");

  return createPortal(
    // z-70 — above every overlay. A toast is how the app reports what just
    // happened; it used to sit above the bulk-approve bar and bury the Undo
    // button that was the only way to reverse the action it was announcing.
    // See `Z` in ui/overlay.tsx for the full stacking order.
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-70 flex flex-col items-center gap-2 pointer-events-none w-full max-w-md px-4">
      <div role="status" aria-live="polite" className="contents">
        {polite.map((t) => (
          <ToastRow key={t.id} toast={t} onDismiss={onDismiss} />
        ))}
      </div>
      <div role="alert" aria-live="assertive" className="contents">
        {assertive.map((t) => (
          <ToastRow key={t.id} toast={t} onDismiss={onDismiss} />
        ))}
      </div>
    </div>,
    document.body
  );
}

// `error` used to be `bg-red-600` while every other error surface in the app was
// rose — two different reds for one meaning. All three now come from the status
// tokens, so they track the theme.
const TONE_STYLE: Record<ToastTone, { wrap: string; Icon: typeof Info }> = {
  default: { wrap: "bg-surface-inverse text-inverse", Icon: Info },
  success: { wrap: "bg-success-solid text-white", Icon: CheckCircle2 },
  error: { wrap: "bg-danger-solid text-white", Icon: AlertCircle },
};

function ToastRow({
  toast,
  onDismiss,
}: {
  toast: Toast;
  onDismiss: (id: string) => void;
}) {
  const [paused, setPaused] = useState(false);
  const [busy, setBusy] = useState(false);
  const { wrap, Icon } = TONE_STYLE[toast.tone];

  // Held in a ref so a re-rendered parent (a new `onDismiss` closure) does not
  // restart the countdown — a toast that keeps resetting never goes away.
  // Assigned in an effect, not during render: refs are not render-safe.
  const dismissRef = useRef(onDismiss);
  useEffect(() => {
    dismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    if (toast.duration === 0 || paused) return;
    const timer = setTimeout(() => dismissRef.current(toast.id), toast.duration);
    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, paused]);

  async function runAction() {
    if (!toast.action || busy) return;
    setBusy(true);
    try {
      await toast.action.onAction();
      dismissRef.current(toast.id);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      // Pausing on focus as well as hover matters: a keyboard user tabbing
      // toward the Undo button is exactly the person most likely to lose it.
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      // `animate-slide-up` is a real keyframe now. This was `animate-in`, which
      // ships with tailwindcss-animate — a plugin that was never installed — so
      // toasts appeared with no transition at all. The reduced-motion guard in
      // styles.css neutralises it globally, which is why there is no
      // `motion-safe:` prefix here any more.
      className={`pointer-events-auto w-full rounded-lg shadow-e3 px-4 py-3 flex items-center gap-3 text-sm animate-slide-up ${wrap}`}
    >
      <Icon className="w-4 h-4 shrink-0" aria-hidden />
      <div className="flex-1">{toast.message}</div>
      {toast.action && (
        <button
          type="button"
          onClick={() => void runAction()}
          disabled={busy}
          className="font-semibold underline underline-offset-2 shrink-0 disabled:opacity-60"
        >
          {busy ? "…" : toast.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss"
        className="shrink-0 opacity-70 hover:opacity-100"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
